/**
 * Claim API Endpoints
 *
 * This service provides methods to interact with the Claims Portal BFF API.
 * - claimData field (stepData removed)
 * - corporateClientName extracted from JWT (not sent in body)
 * - contractNumbers array for drafts query
 * - DEV-7027: response no longer carries `caseId` or dual-state flags
 */

import axiosInstance from '../axiosInstance';
import type {
  ClaimCreateRequest,
  ClaimUpdateRequest,
  ClaimDataResponse,
  ClaimListResponse,
  DraftsQueryParams,
} from '../../../types/api';

/**
 * Create a new claim
 *
 * POST /api/v1/claims
 *
 * @param request - Claim creation request
 * @returns Promise<ClaimDataResponse>
 */
export async function createClaim(
  request: ClaimCreateRequest
): Promise<ClaimDataResponse> {
  const response = await axiosInstance.post<any>(
    '/api/v1/claims',
    request
  );
  // Backend returns wrapped response: { payload: {...}, success: true, messages: {...} }
  return response.data?.payload || response.data;
}

/**
 * Create a new claim on an existing assistance event.
 *
 * POST /api/v1/claims/on-existing-event/{eventNumber}
 *
 * No body and no contract number: the event already carries its contract, so the BFF
 * authorizes the call against the event's own contract and re-derives it from NOVA.
 *
 * @param eventNumber - Event__c.Name of the existing event to link the new claim to
 * @returns Promise<ClaimDataResponse>
 */
export async function createClaimOnExistingEvent(
  eventNumber: string
): Promise<ClaimDataResponse> {
  const response = await axiosInstance.post<any>(
    `/api/v1/claims/on-existing-event/${encodeURIComponent(eventNumber)}`
  );
  // Backend returns wrapped response: { payload: {...}, success: true, messages: {...} }
  return response.data?.payload || response.data;
}

/**
 * Update an existing claim
 *
 * PUT /api/v1/claims/{eventId}
 *
 * @param eventId - Salesforce Event__c ID
 * @param request - Claim update request
 * @returns Promise<ClaimDataResponse>
 */
export async function updateClaim(
  eventId: string,
  request: ClaimUpdateRequest
): Promise<ClaimDataResponse> {
  const response = await axiosInstance.put<any>(
    `/api/v1/claims/${eventId}`,
    request
  );
  // Backend returns wrapped response: { payload: {...}, success: true, messages: {...} }
  return response.data?.payload || response.data;
}

/**
 * Get a claim by ID
 *
 * GET /api/v1/claims/{eventId}
 *
 * TenantContext automatically extracts corporateClientName from JWT
 *
 * @param eventId - Salesforce Event__c ID
 * @returns Promise<ClaimDataResponse>
 */
export async function getClaim(eventId: string, submissionId?: string): Promise<ClaimDataResponse> {
  const params: Record<string, string> = {};
  if (submissionId) params.submissionId = submissionId;

  const response = await axiosInstance.get<any>(
    `/api/v1/claims/${eventId}`,
    { params }
  );
  // Backend returns wrapped response: { payload: {...}, success: true, messages: {...} }
  return response.data?.payload || response.data;
}

/**
 * Get draft claims for given contract numbers
 *
 * GET /api/v1/claims/drafts?contractNumbers=CT-001&contractNumbers=CT-002
 *
 * @param params - Query parameters with contract numbers array
 * @returns Promise<ClaimDataResponse[]>
 */
export async function getDraftClaims(
  params: DraftsQueryParams
): Promise<ClaimDataResponse[]> {
  const response = await axiosInstance.get<any>(
    '/api/v1/claims/drafts',
    {
      params: {
        contractNumbers: params.contractNumbers,
      },
      paramsSerializer: {
        indexes: null, // Use "contractNumbers=CT-001&contractNumbers=CT-002" format
      },
    }
  );

  // Backend returns wrapped response: { payload: { drafts: [...] }, success: true, messages: {...} }
  // Extract the drafts array from the payload
  return response.data?.payload?.drafts || [];
}

/**
 * Submit a claim for processing
 *
 * POST /api/v1/claims/{eventId}/submit
 *
 * This sets:
 * - Event__c.Date_Time_Opened__c to current timestamp
 * - Event__c.Portal_Progress_Percentage__c to 100
 * - Case.Status to "NEW"
 *
 * @param eventId - Salesforce Event__c ID
 * @returns Promise<ClaimDataResponse> with updated status
 */
/**
 * Get unified claim list for the Home page (Brouillons + Envoyées in a single call).
 *
 * GET /api/v1/claims/list?contractNumbers=CT-001&contractNumbers=CT-002
 *
 * @returns Promise<ClaimListResponse> with drafts and sentByYear
 */
export async function getClaimList(): Promise<ClaimListResponse> {
  const contractNumbers = (window as any).parentContext?.contractNumbers || [];

  if (contractNumbers.length === 0) {
    console.warn('[API] No contract numbers found in parent context');
    return {
      drafts: [],
      sentByYear: {},
      draftCount: 0,
      sentCount: 0,
      hasNotifications: false,
      notificationCount: 0,
    };
  }

  const response = await axiosInstance.get<any>(
    '/api/v1/claims/list',
    {
      params: { contractNumbers },
      paramsSerializer: { indexes: null },
      // HomeScreen renders an inline error Alert from claimStore.error when
      // this fails — the global toast would be duplicate noise.
      suppressGlobalErrorToast: true,
    }
  );

  return response.data?.payload || {
    drafts: [],
    sentByYear: {},
    draftCount: 0,
    sentCount: 0,
    hasNotifications: false,
    notificationCount: 0,
  };
}

export async function submitClaim(eventId: string): Promise<ClaimDataResponse> {
  const response = await axiosInstance.post<any>(
    `/api/v1/claims/${eventId}/submit`
  );
  // Backend returns wrapped response: { payload: {...}, success: true, messages: {...} }
  return response.data?.payload || response.data;
}

/**
 * Cancel a claim (abandon draft)
 *
 * POST /api/v1/claims/{eventId}/cancel
 *
 * This sets:
 * - Event__c.Status__c to "CANCELLED"
 * - Case.Status to "Cancelled" (if Case exists)
 *
 * @param eventId - Salesforce Event__c ID
 * @returns Promise<ClaimDataResponse> with updated status
 */
export async function cancelClaim(eventId: string): Promise<ClaimDataResponse> {
  const response = await axiosInstance.post<any>(
    `/api/v1/claims/${eventId}/cancel`
  );
  // Backend returns wrapped response: { payload: {...}, success: true, messages: {...} }
  return response.data?.payload || response.data;
}

/**
 * Delete a claim — system-driven cleanup (DEV-7217).
 *
 * POST /api/v1/claims/{eventId}/delete
 *
 * Sets Portal_Submission_Status__c=DELETED on the targeted submission. Distinct from
 * {@link cancelClaim} (ABANDONED), which is user-initiated. Used by the duplicate-pop-up
 * "continue with existing" flow to discard the new draft the user just started when they
 * choose to resume an existing draft, submission, or NOVA case. DELETED items are hidden
 * everywhere — not surfaced in the home list and not counted in metrics.
 *
 * @param eventId - Salesforce Event__c ID of the new (now-discarded) claim
 * @returns Promise<ClaimDataResponse> with updated status
 */
export async function deleteClaim(eventId: string): Promise<ClaimDataResponse> {
  const response = await axiosInstance.post<any>(
    `/api/v1/claims/${eventId}/delete`
  );
  return response.data?.payload || response.data;
}

/**
 * Append data to an already-submitted claim.
 *
 * POST /api/v1/claims/{eventId}/append
 *
 * Generic endpoint that accepts an {@link AppendType} discriminator and a partial claimData
 * payload. Used by the "append expenses" flow (appendType=ADD_EXPENSE) to add new expenses on
 * top of a submitted medical claim without creating a duplicate NOVA case.
 */
export type AppendType = 'ADD_EXPENSE' | 'ADD_DOCUMENT';

export interface AppendClaimRequest {
  contractNumber: string;
  appendType: AppendType;
  claimData: Record<string, unknown>;
}

export async function appendClaim(
  eventId: string,
  request: AppendClaimRequest
): Promise<ClaimDataResponse> {
  const response = await axiosInstance.post<any>(
    `/api/v1/claims/${eventId}/append`,
    request
  );
  return response.data?.payload || response.data;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Save claim progress (combines create/update logic)
 *
 * If eventId exists, updates the claim. Otherwise, creates a new claim.
 *
 * @param claimData - Complete claim data
 * @param eventId - Optional event ID (for updates)
 * @returns Promise<ClaimDataResponse>
 */
export async function saveClaimProgress(
  claimData: Record<string, any>,
  progressPercentage: number,
  screenCode: string,
  flowType?: 'MEDICAL' | 'CANCELLATION' | 'BAGGAGE',
  eventId?: string,
  portalApprovedAmount?: number
): Promise<ClaimDataResponse> {
  if (eventId) {
    // Update existing claim
    return updateClaim(eventId, {
      screenCode,
      claimData,
      progressPercentage,
      flowType,
      portalApprovedAmount,
    });
  } else {
    // Create new claim - extract contractNumber from claimData
    const contractNumber = claimData.contractNumber;
    if (!contractNumber) {
      throw new Error('Contract number is required to create a claim');
    }

    return createClaim({
      screenCode,
      contractNumber,
      claimData,
      progressPercentage,
      flowType,
    });
  }
}

/**
 * Get active claims for current user's contracts
 *
 * Retrieves active claims (SUBMITTED, IN_PROGRESS, COMPLETED, REVISION_COMPLETED, Closed)
 * excluding Draft and CANCELLED statuses.
 *
 * @returns Promise<Record<string, ClaimDataResponse[]>> - Map of years to claim lists
 */
export async function getActiveClaimsForUser(): Promise<Record<string, ClaimDataResponse[]>> {
  const contractNumbers = (window as any).parentContext?.contractNumbers || [];

  if (contractNumbers.length === 0) {
    console.warn('[API] No contract numbers found in parent context');
    return {};
  }

  const response = await axiosInstance.get<any>(
    '/api/v1/claims/active',
    {
      params: {
        contractNumbers,
      },
      paramsSerializer: {
        indexes: null, // Use "contractNumbers=CT-001&contractNumbers=CT-002" format
      },
    }
  );

  // Backend returns wrapped response: { payload: { "2025": [...], "2024": [...] }, success: true, messages: {...} }
  return response.data?.payload || {};
}

/**
 * Get all drafts for current user's contracts
 *
 * Extracts contract numbers from parent app context
 * Returns user-initiated draft claims (TO_BE_COMPLETED, Draft, IN_REVISION) within 1 year
 * and CANCELLED claims within 3 months.
 *
 * Excludes: NEW and OPEN statuses (Salesforce-created claims not started by user)
 *
 * @returns Promise<ClaimDataResponse[]>
 */
export async function getCurrentUserDrafts(): Promise<ClaimDataResponse[]> {
  const contractNumbers = (window as any).parentContext?.contractNumbers || [];

  if (contractNumbers.length === 0) {
    console.warn('[API] No contract numbers found in parent context');
    return [];
  }

  return getDraftClaims({ contractNumbers });
}

// ============================================================================
// Dev Tools - Test Endpoints (only available in local/dev backend profiles)
// ============================================================================

/**
 * Test the mapping of Portal claim data to Nova format
 *
 * POST /api/v1/claims/testMapper/{eventId}/{caseId}
 *
 * This endpoint is only available in local/dev backend profiles.
 * It tests the ClaimToNovaMapper to validate the transformation logic.
 *
 * @param eventId - Salesforce Event__c ID
 * @param caseId - Salesforce Case ID
 * @param claimData - Portal claim data (Portal_Data__c structure)
 * @returns Promise<any> - Nova JSON payload
 */
export async function testClaimMapper(
  eventId: string,
  caseId: string,
  claimData: Record<string, any>
): Promise<any> {
  const response = await axiosInstance.post<any>(
    `/api/v1/claims/testMapper/${eventId}/${caseId}`,
    claimData
  );

  // Return the Nova JSON payload (parsed object, not stringified)
  return response.data;
}
